/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Projects a model's variant set into the rows of the PDP specifications panel.
 *
 * The panel describes the MODEL, not the product being viewed: each attribute is collapsed across
 * every variant the master has, which is why a shock whose SKUs are individually Trunnion or Standard
 * renders `Shaft Eyelet: Trunnion, Standard` — a string no single product carries.
 *
 * Consequence to internalise before editing: a master URL and a variant URL of the same model produce
 * identical output, because they resolve the same set. There is no master/variant branch here and
 * there should never be one.
 *
 * WHICH attributes to project is not decided here. This module takes them as an argument and holds no
 * list of its own — see `specification-attributes.ts`, which derives them from the catalogue.
 */
import type { SpecificationAttribute } from './specification-attributes';
import type { ShopperProducts } from '@/scapi';

type Product = ShopperProducts.schemas['Product'];

/** One rendered row: an attribute, its label, and its distinct values across the set. */
export interface SpecificationRow {
    /** The merchant attribute id, retained so consumers can key and test without matching on labels. */
    attributeId: string;
    /** Display text, never a raw `c_` id. */
    label: string;
    /**
     * Distinct values in first-seen order, never empty. Kept as an ARRAY and joined only at render:
     * real values contain commas (`Cog sizes: 10-24t:10,12,14,16,18,21,24` is ONE value), so a joined
     * string makes boundaries unrecoverable — and the separator is English punctuation this module
     * has no locale for.
     */
    values: string[];
}

/**
 * Reads one merchant attribute, following the convention the other `c_*` readers use: strings only,
 * ends trimmed, blank and non-string absent. An array-valued attribute reads as absent rather than
 * rendering `[object Object]`.
 *
 * @param product - The product to read from
 * @param attributeId - The merchant attribute id
 * @returns The trimmed value, or `undefined` when unset, blank, or not a string
 */
export function readSpecificationValue(product: Product | null | undefined, attributeId: string): string | undefined {
    const value = product?.[attributeId];

    if (typeof value !== 'string') return undefined;

    const trimmed = value.trim();

    return trimmed === '' ? undefined : trimmed;
}

/**
 * Collapses a variant set into the panel's rows.
 *
 * Values dedupe preserving FIRST APPEARANCE rather than sorting: sorting invents a ranking the
 * catalogue does not have and reorders an existing row when an unrelated variant is added.
 *
 * An attribute no member carries is omitted entirely. That is what lets the caller pass a category's
 * whole attribute group without auditing which fields the catalogue has actually populated — an
 * attribute defined in the schema but empty everywhere costs one loop iteration and renders nothing.
 *
 * @param variantSet - Every variant of the model; a single-element set for a standalone product
 * @param attributes - The attributes to project, in render order, each carrying its resolved label
 * @returns The rows to render, in the given order, with unpopulated attributes omitted
 */
export function computeSpecifications(
    variantSet: readonly Product[],
    attributes: readonly SpecificationAttribute[]
): SpecificationRow[] {
    const rows: SpecificationRow[] = [];

    for (const attribute of attributes) {
        const values = new Set<string>();

        for (const product of variantSet) {
            const value = readSpecificationValue(product, attribute.id);

            if (value !== undefined) values.add(value);
        }

        if (values.size === 0) continue;

        rows.push({
            attributeId: attribute.id,
            label: attribute.label,
            values: [...values],
        });
    }

    return rows;
}
